import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getClientes, getEstadosTareasResumen, getProyectos, getTodosFichajes, getTrabajadores } from '../../services/apis';
import { filtrarProyectosVisibles, ordenarOpcionesPorLabel, textoMayusculas } from '../../utils/utils';
import { SubHeader } from '../../utils/componentes-utiles/SubHeader';
import { ResumenTareasRealizadas } from './ResumenTareasRealizadas';

const obtenerFechaHoy = () => {
    const hoy = new Date();
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const HorasInvertidasEnTareas = () => {
    const fechaHoy = obtenerFechaHoy();
    const [loaded, setLoaded] = useState(false);
    const [loadedFilters, setLoadedFilters] = useState(false);
    const [filtros, setFiltros] = useState({ fechaDesde: fechaHoy, fechaHasta: fechaHoy });
    const [resumenTareas, setResumenTareas] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [opcionesUsuarios, setOpcionesUsuarios] = useState([]);
    const [selectedClientes, setSelectedClientes] = useState([]);
    const [opcionesClientes, setOpcionesClientes] = useState([]);
    const [opcionesProyectos, setOpcionesProyectos] = useState([]);

    useEffect(() => {
        if (filtros.fechaDesde && filtros.fechaHasta) {
            consultarTareas();
        }
    }, [filtros]);

    useEffect(() => {
        cargarTrabajadores();
        cargarClientes();
        cargarProyectos();
    }, []);

    const consultarTareas = async () => {
        try {
            setLoadedFilters(false);
            const response = await getTodosFichajes(filtros.fechaDesde, filtros.fechaHasta, []);
            const tareasResumen = response?.data?.resumenTareas || [];
            const idsTareas = [...new Set(tareasResumen.map(tarea => Number(tarea.id_tarea)).filter(Boolean))];
            let tareasConEstado = tareasResumen;

            if (idsTareas.length > 0) {
                const responseEstados = await getEstadosTareasResumen(idsTareas);
                const estadosPorTarea = new Map(
                    (responseEstados?.data || []).map(estado => [Number(estado.id_tarea), estado])
                );

                tareasConEstado = tareasResumen.map(tarea => ({
                    ...tarea,
                    ...(estadosPorTarea.get(Number(tarea.id_tarea)) || {})
                }));
            }

            setResumenTareas(tareasConEstado);
            setLoaded(true);
            setLoadedFilters(true);
        } catch (error) {
            console.error('Error al consultar las tareas realizadas:', error);
        }
    };

    const cargarTrabajadores = async () => {
        try {
            const response = await getTrabajadores();
            const opciones = response.data.map(item => ({
                label: item.nombre,
                value: item.id
            }));
            setOpcionesUsuarios(opciones);
        } catch (error) {
            console.error('Error al cargar trabajadores', error);
        }
    };

    const cargarClientes = async () => {
        try {
            const response = await getClientes();
            const opciones = response.data.map(item => ({
                label: item.nombre,
                value: item.id
            }));
            setOpcionesClientes(opciones);
        } catch (error) {
            console.error('Error al cargar clientes', error);
        }
    };

    const cargarProyectos = async () => {
        try {
            const response = await getProyectos();
            const opciones = ordenarOpcionesPorLabel(filtrarProyectosVisibles(response?.data || []).map(item => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
            })));

            setOpcionesProyectos(opciones);
        } catch (error) {
            console.error('Error al cargar proyectos', error);
        }
    };

    const actualizarEstadoTareaResumen = (estadoTarea) => {
        if (!estadoTarea?.id_tarea) return;

        setResumenTareas(prev => prev.map(tarea => (
            Number(tarea.id_tarea) === Number(estadoTarea.id_tarea)
                ? { ...tarea, ...estadoTarea }
                : tarea
        )));
    };

    const actualizarProyectoTareaResumen = (proyectoTarea) => {
        if (!proyectoTarea?.id_tarea) return;

        setResumenTareas(prev => prev.map(tarea => (
            Number(tarea.id_tarea) === Number(proyectoTarea.id_tarea)
                ? {
                    ...tarea,
                    id_proyecto: proyectoTarea.id_proyecto,
                    nombre_proyecto: proyectoTarea.nombre_proyecto
                }
                : tarea
        )));
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <SubHeader />
            <Box
                className="fade-in-2"
                sx={{
                    width: '100%',
                    px: { xs: 1.5, sm: 2.5, md: 4 },
                    py: { xs: 2, md: 4 },
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                }}
            >
                {!loaded && <CircularProgress />}
                {loaded && (
                    <ResumenTareasRealizadas
                        tareas={resumenTareas}
                        filtros={filtros}
                        setFiltros={setFiltros}
                        loadedFilters={loadedFilters}
                        opcionesUsuarios={opcionesUsuarios}
                        selectedUsers={selectedUsers}
                        setSelectedUsers={setSelectedUsers}
                        opcionesClientes={opcionesClientes}
                        selectedClientes={selectedClientes}
                        setSelectedClientes={setSelectedClientes}
                        onTareaEstadoCambiado={actualizarEstadoTareaResumen}
                        opcionesProyectos={opcionesProyectos}
                        onTareaProyectoCambiado={actualizarProyectoTareaResumen}
                    />
                )}
            </Box>
        </Box>
    );
};
